<template>
    <div :class="{'file-input-wrapper': !storageBlocked}">
        <template v-if="!storageBlocked">
            <input v-if="!reset" ref="inputRef" :name="name" type="file" :class="size" :id="_id" :required="_required" @change="filesChanged"
                v-bind="{...$attrs, ...ariaValidationAttributes}" aria-label="Datei auswählen"/>
            <span v-if="hasFiles" v-interact="clear" class="icon-interact" aria-label="Dateiauswahl zurücksetzen">
                <i aria-hidden="true" class="material-icons" role="img">clear</i>
            </span>
        </template>
        <template v-else>
            <div class="info"><i class="material-icons md-light" aria-hidden="true" role="img">info</i>
                <div>Die Upload-Funktion ist aktuell gesperrt, da der Speicherplatz überschritten ist.</div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
    import {useAbstractFormInput} from '@/composables/abstractFormInput';
    import {usePortuna} from '@/composables/portuna';
    import {FormRowKey} from '@/library/form';
    import {useStore} from 'vuex';
    import {ref, nextTick, computed, inject, toRefs} from 'vue';

    const portuna = usePortuna();

    const emit = defineEmits<{
        (eventName: 'changed-files', hasFiles: boolean, length: number): void;
    }>();

    const store = useStore();
    const props = withDefaults(
        defineProps<{
            id?: string;
            required?: boolean;
            overwriteRequired?: boolean;
            errorName?: string;
            filterName?: string;
            name: string;
            ignoreStorageBlocked?: boolean;
            size?: string;
        }>(),
        {
            required: false,
            overwriteRequired: false,
            errorName: '',
            filterName: '',
            ignoreStorageBlocked: false,
            size: 'long',
        },
    );

    const parentFormRow = inject<Nullable<IFormRowContext>>(FormRowKey, null);

    const inputRef = ref<HTMLInputElement>();

    const {
              id,
              name,
              required,
              overwriteRequired,
              errorName,
              filterName,
          } = toRefs(props);

    const {
              _id,
              _required,
              ariaValidationAttributes,
              expose,
          } = useAbstractFormInput(
        parentFormRow,
        id,
        required,
        overwriteRequired,
        errorName,
        filterName,
    );

    const hasFiles = ref<boolean>(false);
    const reset    = ref<boolean>(false);

    const storageBlocked = computed(() =>
    {
        return !props.ignoreStorageBlocked && store.state.layoutData.storageBlocked;
    });

    const filesChanged = (event: Event) =>
    {
        const length = (event.target as HTMLInputElement).files!.length;

        hasFiles.value = length > 0;

        emit('changed-files', hasFiles.value, length);
    };

    const clear = (): void =>
    {
        reset.value = true;

        nextTick(() =>
        {
            hasFiles.value = false;
            reset.value    = false;

            emit('changed-files', hasFiles.value, 0);
        });
    };

    const focus = () =>
    {
        inputRef.value && inputRef.value.click();
    };

    defineExpose(Object.assign(
        {},
        portuna,
        expose,
        {
            componentName: 'FormInputFile',
            focus,
            clear,
            hasFiles,
        }
    ));
</script>